import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Images, X } from "lucide-react";

export default function Gallery({ dayId, photos }) {
  const [index, setIndex] = useState(null);
  const open = index !== null;

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setIndex(null);
      if (e.key === "ArrowRight") setIndex((i) => (i + 1) % photos.length);
      if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, photos.length]);

  if (!photos?.length) return null;

  return (
    <div data-testid={`day-${dayId}-gallery`}>
      <div className="font-accent text-xs text-[#D96C4A] mb-4 flex items-center gap-2">
        <Images className="w-4 h-4" />
        Galleria
      </div>
      <h2 className="font-display text-3xl text-[#1D3A4C] mb-8">
        I luoghi di oggi
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {photos.map((p, i) => (
          <button
            key={i}
            type="button"
            data-testid={`day-${dayId}-gallery-thumb-${i}`}
            onClick={() => setIndex(i)}
            className="group relative aspect-square overflow-hidden rounded-xl border border-[#1D3A4C]/10"
          >
            <img
              src={p.url}
              alt={p.caption}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="absolute bottom-2 left-2 right-2 text-xs text-white font-medium opacity-0 group-hover:opacity-100 transition-opacity truncate">
              {p.caption}
            </div>
          </button>
        ))}
      </div>

      {open && (
        <div
          data-testid={`day-${dayId}-gallery-lightbox`}
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center"
          onClick={() => setIndex(null)}
        >
          <button
            type="button"
            aria-label="Chiudi"
            onClick={() => setIndex(null)}
            className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
          >
            <X className="w-5 h-5" />
          </button>

          <button
            type="button"
            aria-label="Precedente"
            onClick={(e) => {
              e.stopPropagation();
              setIndex((i) => (i - 1 + photos.length) % photos.length);
            }}
            className="absolute left-3 md:left-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <figure
            className="max-w-4xl max-h-[80vh] mx-16 flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={photos[index].url}
              alt={photos[index].caption}
              className="max-w-full max-h-[70vh] object-contain rounded-lg"
            />
            <figcaption className="mt-4 text-white/85 text-sm font-accent tracking-wide">
              {photos[index].caption} · {index + 1}/{photos.length}
            </figcaption>
          </figure>

          <button
            type="button"
            aria-label="Successiva"
            onClick={(e) => {
              e.stopPropagation();
              setIndex((i) => (i + 1) % photos.length);
            }}
            className="absolute right-3 md:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      )}
    </div>
  );
}
